import React, { useState } from 'react';
import { ChevronRight, Check, Sparkles, Crown, Shield, Lock, User, Bell, Trash2, AlertTriangle, Save, Mail, Settings } from 'lucide-react';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { ChangeEmailModal } from '../components/Settings/ChangeEmailModal';
import { TwoFactorModal } from '../components/Settings/TwoFactorModal';
import { PrivacySettingsModal } from '../components/Settings/PrivacySettingsModal';
import { SubscriptionModal } from '../components/Settings/SubscriptionModal';
import { DeleteAccountModal } from '../components/Settings/DeleteAccountModal';
import { BillingManagementModal } from '../components/Settings/BillingManagementModal';
import { CancelSubscriptionModal } from '../components/Settings/CancelSubscriptionModal';
import { deleteUserAccount } from '../services/accountService';
import { usePlanEntitlement } from '../hooks/usePlanEntitlement';
export function SettingsPage() {
  const navigate = useNavigate();
  const {
    user,
    signOut,
    updateUserProfile
  } = useAuth();
  const {
    showToast
  } = useToast();
  const [formData, setFormData] = useState({
    firstName: user?.user_metadata?.firstName || '',
    lastName: user?.user_metadata?.lastName || '',
    email: user?.email || ''
  });
  const [emailNotifications, setEmailNotifications] = useState(user?.user_metadata?.emailNotifications ?? true);
  const [pushNotifications, setPushNotifications] = useState(user?.user_metadata?.pushNotifications ?? true);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  // The user's real plan and scan entitlement — nothing here is hardcoded.
  const {
    planId,
    planLabel,
    priceLabel,
    billingLabel,
    accessLabel,
    statusLabel,
    unlimited,
    renewalDate,
    loading: loadingProfile
  } = usePlanEntitlement();
  const handleSaveChanges = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await updateUserProfile({
        firstName: formData.firstName,
        lastName: formData.lastName
      });
      setSuccess('Profile updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };
  const handleNotificationChange = async (type: 'email' | 'push', value: boolean) => {
    setError(null);
    setSuccess(null);
    if (type === 'email') {
      setEmailNotifications(value);
    } else {
      setPushNotifications(value);
    }
    try {
      await updateUserProfile({
        emailNotifications: type === 'email' ? value : emailNotifications,
        pushNotifications: type === 'push' ? value : pushNotifications
      });
      setSuccess(`${type === 'email' ? 'Email' : 'Push'} notifications ${value ? 'enabled' : 'disabled'}`);
      setTimeout(() => setSuccess(null), 2000);
    } catch (err: any) {
      if (type === 'email') {
        setEmailNotifications(!value);
      } else {
        setPushNotifications(!value);
      }
      setError(`Failed to update ${type} notification settings`);
    }
  };
  const handleLogout = async () => {
    await signOut();
    navigate('/');
    showToast('Logged out successfully', 'success');
  };
  const handleDeleteAccount = async () => {
    if (!user) return;
    setDeleteLoading(true);
    setError(null);
    try {
      await deleteUserAccount(user.id);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Failed to delete account');
      setShowDeleteAccountModal(false);
    } finally {
      setDeleteLoading(false);
    }
  };
  return <div className="flex flex-col w-full min-h-screen bg-[#050505] relative overflow-hidden" data-id="element-9817" data-mp="1hivby0amajx0">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none fixed" data-id="element-9818" data-mp="1vajlbvtheuaa" />

      <WebsiteHeader data-id="element-9819" data-mp="20bz7xsbb58us" />

      <div className="max-w-3xl mx-auto w-full relative z-10" data-id="element-9820" data-mp="208l38df68nk6">
        <main className="px-6 pt-12 pb-24" data-id="element-9821" data-mp="2jxiwvey188u9">
          {/* Page Header */}
          <div className="mb-12" data-id="element-9822" data-mp="2ny2t8gas78fc">
            <div className="inline-flex items-center gap-2 mb-4 bg-[#FF0000]/10 border border-[#FF0000]/30 px-3 py-1" data-id="element-9823" data-mp="2nx9mhfbtoix7">
              <Settings size={14} className="text-[#FF0000]" data-id="element-9824" data-mp="2a04kprj5667r" />
              <span className="text-[#FF0000] font-mono text-xs tracking-widest uppercase font-bold" data-id="element-9825" data-mp="2evtjvtr91l3j">
                System Configuration
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-white mb-2 uppercase tracking-tight" data-id="element-9826" data-mp="24z70zaihh445">
              Settings & Preferences
            </h1>
            <p className="text-gray-400 font-mono text-sm max-w-xl" data-id="element-9827" data-mp="1r5fsm5ghnqa0">
              Manage your account parameters, security protocols, and
              notification preferences.
            </p>
          </div>

          {/* Success/Error Messages */}
          {success && <div className="bg-green-500/10 border border-green-500/30 p-4 mb-8 flex items-center gap-3" data-id="element-9828" data-mp="1m7xs93gjejdm">
              <Check className="text-green-500 flex-shrink-0" size={20} data-id="element-9829" data-mp="1355dzlvy906t" />
              <p className="text-green-500 text-sm font-mono uppercase tracking-wide" data-id="element-9830" data-mp="ycc7asgx7hs3">
                {success}
              </p>
            </div>}
          {error && <div className="bg-[#FF0000]/10 border border-[#FF0000]/30 p-4 mb-8 flex items-center gap-3" data-id="element-9831" data-mp="ofpp6bfzb4tr">
              <AlertTriangle className="text-[#FF0000] flex-shrink-0" size={20} data-id="element-9832" data-mp="1m1nbkjxhbuaz" />
              <p className="text-[#FF0000] text-sm font-mono uppercase tracking-wide" data-id="element-9833" data-mp="1h4ddygzxscc2">
                {error}
              </p>
            </div>}

          {/* Subscription & Billing */}
          <section aria-labelledby="billing-heading" className="mb-8" data-id="element-9834" data-mp="17561dw110lq0">
            <div className="mb-5" data-id="element-9835" data-mp="qeth8dhk0mh">
              <h2 id="billing-heading" className="text-xl font-black text-white uppercase tracking-tight" data-id="element-9836" data-mp="n94ru8rr0ra">
                Subscription & Billing
              </h2>
              <p className="mt-1.5 text-gray-400 font-mono text-xs" data-id="element-9837" data-mp="27brnz8tszfvp">
                Manage your ToxicDetector plan, scan access, and billing.
              </p>
            </div>

            <div className="bg-[#0a0a0a] border border-gray-800 p-8 relative group" data-id="element-9838" data-mp="2hah7t759fael">
              {/* Corner Brackets */}
              <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9839" data-mp="2m4bk3pijcn3n" />
              <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9840" data-mp="2v25ir73rvstz" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9841" data-mp="300gr1def5va3" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9842" data-mp="15p1x0kedl9oj" />

              <div className="flex items-start gap-4" data-id="element-9843" data-mp="1apg0rq8y1xgr">
                <div className={`flex-shrink-0 w-12 h-12 flex items-center justify-center border ${unlimited ? 'bg-[#FFD700]/10 border-[#FFD700] text-[#FFD700]' : planId === 'single_scan' ? 'bg-[#FF0000]/10 border-[#FF0000] text-[#FF0000]' : 'bg-gray-900 border-gray-700 text-gray-500'}`} data-id="element-9844" data-mp="3cju7y75uved8">
                  {unlimited || planId === 'single_scan' ? <Crown size={24} data-id="element-9845" data-mp="2oohr662g4tof" /> : <Sparkles size={24} data-id="element-9846" data-mp="33kh8867ja45p" />}
                </div>
                <div className="flex-1 min-w-0" data-id="element-9847" data-mp="2yldzobschfsz">
                  <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500" data-id="element-9848" data-mp="2yjjgp30mvxrj">
                    Current plan
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-3" data-id="element-9849" data-mp="dvez4ndmq90e">
                    <h3 className="text-2xl font-black text-white uppercase tracking-tight" data-id="element-9850" data-mp="leeg6mdmbogl">
                      {loadingProfile ? 'Loading…' : planLabel}
                    </h3>
                    {!loadingProfile && <span className={`px-2 py-1 font-mono text-[9px] font-black uppercase tracking-[0.15em] ${statusLabel === 'Active' ? 'bg-green-500/10 border border-green-500/40 text-green-400' : statusLabel === 'Free' ? 'bg-gray-900 border border-gray-700 text-gray-400' : 'bg-[#FF0000]/10 border border-[#FF0000]/40 text-[#FF0000]'}`} data-id="element-9851" data-mp="cehwz09zzhsx">
                        {statusLabel}
                      </span>}
                  </div>
                  <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2" data-id="element-9852" data-mp="1rugnkxnkmv5f">
                    <div data-id="element-9853" data-mp="1rwbbmku533dq">
                      <dt className="font-mono text-[10px] uppercase tracking-[0.15em] text-gray-600" data-id="element-9854" data-mp="3b3emogimxvq0">
                        Price
                      </dt>
                      <dd className="mt-1 font-mono text-sm text-white" data-id="element-9855" data-mp="3g0ybubgvfswq">
                        {priceLabel}
                      </dd>
                    </div>
                    <div data-id="element-9856" data-mp="363tow9f7btev">
                      <dt className="font-mono text-[10px] uppercase tracking-[0.15em] text-gray-600" data-id="element-9857" data-mp="3630dgv5asfqe">
                        Billing
                      </dt>
                      <dd className="mt-1 font-mono text-sm text-white" data-id="element-9858" data-mp="2x4ewv5jdzas9">
                        {billingLabel}
                      </dd>
                    </div>
                    <div data-id="element-9859" data-mp="2ned96euy3udv">
                      <dt className="font-mono text-[10px] uppercase tracking-[0.15em] text-gray-600" data-id="element-9860" data-mp="2ndtzflfg8cax">
                        Scan access
                      </dt>
                      <dd className="mt-1 font-mono text-sm text-white" data-id="element-9861" data-mp="29gygn19eb6ho">
                        {loadingProfile ? 'Verifying clearance…' : accessLabel}
                      </dd>
                    </div>
                    {renewalDate && <div data-id="element-9862" data-mp="1ptu6wgq5s1xj">
                        <dt className="font-mono text-[10px] uppercase tracking-[0.15em] text-gray-600" data-id="element-9863" data-mp="1pun4ar389f30">
                          Renews
                        </dt>
                        <dd className="mt-1 font-mono text-sm text-white" data-id="element-9864" data-mp="1yt7ef3uq2gfm">
                          {renewalDate}
                        </dd>
                      </div>}
                  </dl>
                </div>
              </div>

              <div className="mt-7 flex flex-col sm:flex-row gap-4" data-id="element-9865" data-mp="28ptzntqyn9i6">
                <button onClick={() => setShowSubscriptionModal(true)} className="flex-1 bg-[#FF0000] text-white py-3 font-bold hover:bg-[#D90000] transition-colors duration-200 ease-out text-xs uppercase tracking-widest shadow-[0_0_15px_rgba(255,0,0,0.3)]" data-id="element-9866" data-mp="2mijqqclcj4m5">
                  {unlimited ? 'View plans' : 'Upgrade plan'}
                </button>
                <button onClick={() => setShowBillingModal(true)} className="flex-1 bg-transparent border border-gray-700 text-gray-300 py-3 font-bold hover:border-gray-500 hover:text-white transition-colors duration-200 ease-out text-xs uppercase tracking-widest" data-id="element-9867" data-mp="1p6b6tn722lj8">
                  Billing history
                </button>
              </div>
            </div>
          </section>

          {/* Account Information */}
          <div className="bg-[#0a0a0a] border border-gray-800 p-8 mb-8 relative group" data-id="element-9868" data-mp="2ctj87u1d5afz">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9869" data-mp="2gwfld584wecj" />
            <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9870" data-mp="mhwmrdqpquzs" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9871" data-mp="rhr9r67db0fk" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9872" data-mp="jz9uejct8reo" />

            <div className="flex items-center gap-3 mb-6" data-id="element-9873" data-mp="a8p19bfwof35">
              <User size={20} className="text-[#FF0000]" data-id="element-9874" data-mp="58kmbpunbsf1" />
              <h3 className="font-bold text-white uppercase tracking-wider" data-id="element-9875" data-mp="5a5fpc5855bb">
                Identity Parameters
              </h3>
            </div>

            <form onSubmit={handleSaveChanges} className="space-y-6" data-id="element-9876" data-mp="xxhsiull1yrf">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9877" data-mp="xvf1q8clw1ln">
                <div data-id="element-9878" data-mp="swk80bd3osmr">
                  <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-widest font-mono" data-id="element-9879" data-mp="t0833ey71n3l">
                    First Name
                  </label>
                  <input type="text" value={formData.firstName} onChange={e => setFormData({
                  ...formData,
                  firstName: e.target.value
                })} className="w-full px-4 py-3 bg-[#050505] border border-gray-800 focus:outline-none focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] text-white transition-all font-mono text-sm" data-id="element-9880" data-mp="3dpxh5pr2p4v8" />
                </div>
                <div data-id="element-9881" data-mp="1k42cdehtlv8o">
                  <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-widest font-mono" data-id="element-9882" data-mp="1k5luzbqy8idm">
                    Last Name
                  </label>
                  <input type="text" value={formData.lastName} onChange={e => setFormData({
                  ...formData,
                  lastName: e.target.value
                })} className="w-full px-4 py-3 bg-[#050505] border border-gray-800 focus:outline-none focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] text-white transition-all font-mono text-sm" data-id="element-9883" data-mp="1b45r7mun3eid" />
                </div>
              </div>

              <div data-id="element-9884" data-mp="3nfgpktkg405i">
                <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-widest font-mono" data-id="element-9885" data-mp="64pe723ep1uh">
                  Email Address
                </label>
                <div className="flex gap-3" data-id="element-9886" data-mp="13tfd2xww5vx">
                  <div className="flex-1 px-4 py-3 bg-[#050505] border border-gray-800 text-gray-400 font-mono text-sm flex items-center" data-id="element-9887" data-mp="39kwklgd7hd6y">
                    <Mail size={14} className="mr-3 text-gray-600" data-id="element-9888" data-mp="34oftqeemgkq6" />
                    {user?.email || ''}
                  </div>
                  <button type="button" onClick={() => setShowChangeEmailModal(true)} className="px-6 py-3 bg-gray-900 border border-gray-700 text-white font-bold hover:bg-gray-800 hover:border-gray-500 transition-all text-xs uppercase tracking-widest whitespace-nowrap" data-id="element-9889" data-mp="3el2el36y7bs7">
                    Change
                  </button>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-800" data-id="element-9890" data-mp="3fkjda2xt9s97">
                <button type="submit" disabled={loading} className="w-full bg-white text-black py-3 font-bold hover:bg-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-xs uppercase tracking-widest flex items-center justify-center gap-2" data-id="element-9891" data-mp="3kc2idx6s3kcj">
                  {loading ? 'Processing Update...' : <>
                      <Save size={16} data-id="element-9892" data-mp="31o5zb3shk8zh" />
                      Save Changes
                    </>}
                </button>
              </div>
            </form>
          </div>

          {/* Notifications */}
          <div className="bg-[#0a0a0a] border border-gray-800 p-8 mb-8 relative group" data-id="element-9893" data-mp="luotrnjatm61">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9894" data-mp="26d94h85hlrfj" />
            <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9895" data-mp="21fzhs09hftve" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9896" data-mp="1romr3bzf79qy" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9897" data-mp="1nnt1me6bxmvy" />

            <div className="flex items-center gap-3 mb-6" data-id="element-9898" data-mp="1do292i1bxhyi">
              <Bell size={20} className="text-[#FF0000]" data-id="element-9899" data-mp="1dok8fnjagcan" />
              <h3 className="font-bold text-white uppercase tracking-wider" data-id="element-9900" data-mp="18q0k5gh82of4">
                Alert Protocols
              </h3>
            </div>

            <div className="space-y-6" data-id="element-9901" data-mp="3808ra0sn4z0b">
              <div className="flex items-center justify-between" data-id="element-9902" data-mp="332qeu0ff01nk">
                <div data-id="element-9903" data-mp="331fgwh8nho4d">
                  <p className="font-bold text-white text-sm uppercase tracking-wide" data-id="element-9904" data-mp="2bxe3hh7i86j4">
                    Email Reports
                  </p>
                  <p className="text-xs text-gray-500 font-mono mt-1" data-id="element-9905" data-mp="2gwhbnais6svb">
                    Receive detailed analysis via secure email
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-9906" data-mp="2764gkodcbhax">
                  <input type="checkbox" checked={emailNotifications} onChange={e => handleNotificationChange('email', e.target.checked)} className="sr-only peer" data-id="element-9907" data-mp="1t6v9ce743xj3" />
                  <div className="w-11 h-6 bg-gray-900 border border-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-500 after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF0000]/20 peer-checked:border-[#FF0000] peer-checked:after:bg-[#FF0000] peer-checked:after:border-[#FF0000]" data-id="element-9908" data-mp="1o7juxbu7x44o"></div>
                </label>
              </div>

              <div className="h-px bg-gray-900" data-id="element-9909" data-mp="1gqvoyc1nrtfh" />

              <div className="flex items-center justify-between" data-id="element-9910" data-mp="1bxu2niz4grlv">
                <div data-id="element-9911" data-mp="1qmymf4lkf3w3">
                  <p className="font-bold text-white text-sm uppercase tracking-wide" data-id="element-9912" data-mp="1qmgs4digqzqt">
                    System Alerts
                  </p>
                  <p className="text-xs text-gray-500 font-mono mt-1" data-id="element-9913" data-mp="1lnm32ttwkey9">
                    Real-time notifications for completed scans
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-9914" data-mp="1upvbne5m1y90">
                  <input type="checkbox" checked={pushNotifications} onChange={e => handleNotificationChange('push', e.target.checked)} className="sr-only peer" data-id="element-9915" data-mp="58ubgj4kzbq0" />
                  <div className="w-11 h-6 bg-gray-900 border border-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-500 after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF0000]/20 peer-checked:border-[#FF0000] peer-checked:after:bg-[#FF0000] peer-checked:after:border-[#FF0000]" data-id="element-9916" data-mp="2zrbsrxno8lp3"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Security */}
          <div className="bg-[#0a0a0a] border border-gray-800 p-8 mb-8 relative group" data-id="element-9917" data-mp="2ptfhn5uqhqqz">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9918" data-mp="a185i1hgfnxu" />
            <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9919" data-mp="dl8mouakjwtg" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9920" data-mp="ho58etveo91m" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9921" data-mp="8lvurv4ubzk0" />

            <div className="flex items-center gap-3 mb-6" data-id="element-9922" data-mp="wcsyikmhfqk6">
              <Shield size={20} className="text-[#FF0000]" data-id="element-9923" data-mp="11ab88d8bq9t8" />
              <h3 className="font-bold text-white uppercase tracking-wider" data-id="element-9924" data-mp="11cnqkrhzum6t">
                Security Protocols
              </h3>
            </div>

            <div className="space-y-4" data-id="element-9925" data-mp="rjzuy02r2o9o">
              <button onClick={() => setShowTwoFactorModal(true)} className="w-full flex items-center justify-between p-4 bg-[#050505] border border-gray-800 hover:border-gray-600 transition-all group/btn" data-id="element-9926" data-mp="rix5zxozmvwe">
                <div className="flex items-center gap-4" data-id="element-9927" data-mp="1txkpd7wr8crj">
                  <div className="p-2 bg-gray-900 border border-gray-800 group-hover/btn:border-gray-600 transition-colors" data-id="element-9928" data-mp="1tuyilahou1ad">
                    <Lock size={16} className="text-gray-400" data-id="element-9929" data-mp="1jylj1vzgot7p" />
                  </div>
                  <span className="font-bold text-gray-300 text-sm uppercase tracking-wide group-hover/btn:text-white" data-id="element-9930" data-mp="1fxa1mdqqreal">
                    Two-Factor Authentication
                  </span>
                </div>
                <ChevronRight size={16} className="text-gray-600 group-hover/btn:text-[#FF0000] transition-colors" data-id="element-9931" data-mp="167qg10bn96c1" />
              </button>

              <button onClick={() => setShowPrivacyModal(true)} className="w-full flex items-center justify-between p-4 bg-[#050505] border border-gray-800 hover:border-gray-600 transition-all group/btn" data-id="element-9932" data-mp="w9alsohblgyb">
                <div className="flex items-center gap-4" data-id="element-9933" data-mp="n94g3orv5l9p">
                  <div className="p-2 bg-gray-900 border border-gray-800 group-hover/btn:border-gray-600 transition-colors" data-id="element-9934" data-mp="2ywhc9oo6ouqb">
                    <Shield size={16} className="text-gray-400" data-id="element-9935" data-mp="33tpwrvwt1auq" />
                  </div>
                  <span className="font-bold text-gray-300 text-sm uppercase tracking-wide group-hover/btn:text-white" data-id="element-9936" data-mp="37xpak6qp9vke">
                    Privacy Configuration
                  </span>
                </div>
                <ChevronRight size={16} className="text-gray-600 group-hover/btn:text-[#FF0000] transition-colors" data-id="element-9937" data-mp="2g6hvdp2hylxz" />
              </button>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="bg-[#FF0000]/5 border border-[#FF0000]/30 p-8 mb-8 relative" data-id="element-9938" data-mp="2twv0zkrx1ok5">
            <h3 className="font-bold text-[#FF0000] mb-4 flex items-center gap-2 uppercase tracking-wider text-sm" data-id="element-9939" data-mp="2typnh3iu5oqc">
              <AlertTriangle size={16} data-id="element-9940" data-mp="21gaidov9dv4o" />
              Critical Zone
            </h3>
            <p className="text-xs text-gray-400 mb-6 font-mono" data-id="element-9941" data-mp="21e7m4n3ij5ri">
              WARNING: Account deletion is irreversible. All forensic data and
              analysis logs will be permanently purged.
            </p>
            <button onClick={() => setShowDeleteAccountModal(true)} className="w-full bg-transparent border border-[#FF0000] text-[#FF0000] py-3 font-bold hover:bg-[#FF0000] hover:text-white transition-all text-xs uppercase tracking-widest flex items-center justify-center gap-2" data-id="element-9942" data-mp="gpws9ken2mz3">
              <Trash2 size={16} data-id="element-9943" data-mp="lq1bhi6xlvt0" />
              Delete Account
            </button>
          </div>

          {/* Logout Button */}
          <button onClick={handleLogout} className="w-full bg-gray-900 border border-gray-800 text-gray-400 py-4 font-bold hover:bg-gray-800 hover:text-white hover:border-gray-600 transition-all text-xs uppercase tracking-widest" data-id="element-9944" data-mp="zo7zm6m2whfh">
            Terminate Session
          </button>
        </main>
      </div>

      {/* Modals */}
      <ChangeEmailModal isOpen={showChangeEmailModal} onClose={() => setShowChangeEmailModal(false)} data-id="element-9945" data-mp="2nalui1l7bq2d" />
      <TwoFactorModal isOpen={showTwoFactorModal} onClose={() => setShowTwoFactorModal(false)} data-id="element-9946" data-mp="2rbxm2cnmyld7" />
      <PrivacySettingsModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} data-id="element-9947" data-mp="2diy7qdzlax78" />
      <SubscriptionModal isOpen={showSubscriptionModal} onClose={() => setShowSubscriptionModal(false)} data-id="element-9948" data-mp="23mutwavdla4g" />
      <DeleteAccountModal isOpen={showDeleteAccountModal} onClose={() => setShowDeleteAccountModal(false)} onConfirm={handleDeleteAccount} loading={deleteLoading} data-id="element-9949" data-mp="28m7t7v3fo9nx" />
      <BillingManagementModal isOpen={showBillingModal} onClose={() => setShowBillingModal(false)} data-id="element-9950" data-mp="1zkhrnwfu7ucf" />
      <CancelSubscriptionModal isOpen={showCancelModal} onClose={() => setShowCancelModal(false)} currentPlan={planId} data-id="element-9951" data-mp="3p1ipwlxb46ss" />
    </div>;
}